import { Marker } from "react-map-gl/mapbox"
import { Avatar } from "antd"
import { UserAddOutlined } from "@ant-design/icons"
import { getInitials } from "../scheduleModel"

export type MarkerStatus = 'planned' | 'clocked-in' | 'visited'

export type Props = {
  latitude: number
  longitude: number
  status: MarkerStatus
  crewLeadAvatar?: string | null
  crewLeadName?: string | null
  crewLeadColor?: string | null
  onClick?: (e: any) => void
  isHovered?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

const getMarkerColors = (status: MarkerStatus) => {
  switch (status) {
    case 'planned':
      return { markerColor: '#ADC6FF', borderColor: '#061178' }
    case 'clocked-in':
      return { markerColor: '#FFE58F', borderColor: '#874D00' }
    case 'visited':
      return { markerColor: '#CCC', borderColor: '#000' }
    default:
      return { markerColor: '#ADC6FF', borderColor: '#061178' }
  }
}

const ScheduleMapMarker = ({
  latitude,
  longitude,
  status,
  crewLeadAvatar,
  crewLeadName,
  crewLeadColor,
  onClick,
  isHovered = false,
  onMouseEnter,
  onMouseLeave
}: Props) => {
  const { borderColor: statusBorderColor } = getMarkerColors(status)
  const borderColor = crewLeadName ? (crewLeadColor || statusBorderColor) : '#820014'

  const markerStyle = {
    position: 'relative',
    width: '50px',
    height: '70px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: onClick || onMouseEnter ? 'pointer' : 'default'
  } as const

  const pinHeadOuterStyle = {
    width: '50px',
    height: '50px',
    backgroundColor: crewLeadName ? borderColor : '#FFF1F0',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: crewLeadName ? 'none' : '2px dashed #820014',
    zIndex: 2,
    position: 'relative',
    boxShadow: isHovered ? '0 0 16px 6px rgba(0,0,0,0.2)' : 'none',
    transition: 'box-shadow 0.2s, transform 0.2s',
    transform: isHovered ? 'scale(1.15)' : 'scale(1)',
  } as const

  const pinHeadInnerStyle = {
    width: '42px',
    height: '42px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  } as const

  const pinTailStyle = {
    width: '30px',
    height: '30px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
    marginTop: '-6px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    zIndex: 1
  }

  const getStatusBadgeColor = () => {
    switch (status) {
      case 'clocked-in':
        return '#52c41a'
      case 'visited':
        return '#8c8c8c'
      case 'planned':
      default:
        return '#1890ff'
    }
  }

  const statusBadgeStyle = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '14px',
    height: '14px',
    backgroundColor: getStatusBadgeColor(),
    borderRadius: '50%',
    border: '2px solid white',
    zIndex: 10,
    animation: status === 'clocked-in' ? 'pulse 2s infinite' : 'none'
  } as const

  const renderContent = () => {
    if (!crewLeadName) {
      return (
        <Avatar
          size={28}
          icon={<UserAddOutlined />}
          style={{
            backgroundColor: '#FFF1F0',
            border: '1px dashed #820014',
            color: '#820014',
            fontSize: '14px'
          }}
        />
      )
    }

    if (crewLeadAvatar) {
      return (
        <Avatar
          src={crewLeadAvatar}
          size={28}
          style={{
            border: 'none',
            fontSize: '12px',
            fontWeight: 600
          }}
        >
          {getInitials(crewLeadName)}
        </Avatar>
      )
    }

    return (
      <div style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: borderColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 600,
        color: '#fff'
      }}>
        {getInitials(crewLeadName)}
      </div>
    )
  }

  return (
    <Marker
      latitude={latitude}
      longitude={longitude}
      anchor="bottom"
    >
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div
        style={markerStyle}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div style={statusBadgeStyle} />
        <div style={pinHeadOuterStyle}>
          <div style={pinHeadInnerStyle}>
            {renderContent()}
          </div>
        </div>
        <div style={pinTailStyle} />
      </div>
    </Marker>
  )
}

export default ScheduleMapMarker
